// @ts-nocheck
import React from "react";
import PubSub from "pubsub-js";
import { eventNames } from "../../constants/eventNames";
import AutoLogout from "../../services/autologout.service";

// PubSub event any component can publish to force an immediate reconnect —
// used by the "Retry" button on the "Websocket connection lost" toast.
export const WEBSOCKET_RETRY_EVENT = "websocket-retry-connect";

// PubSub event WebSockets publishes after handling a Retry request. Payload:
//   'accepted'   — reconnect is now in flight; task should wait
//   'in-flight'  — a previous reconnect is still running; ignored this call
//   'failed'     — constructor threw or component unmounted; nothing to do
// Toasts subscribe to this so the user always sees an outcome for their click,
// never a silent no-op.
export const WEBSOCKET_RETRY_ACK = "websocket-retry-ack";

// PubSub event WebSockets publishes when the auto-reconnect ceiling has been
// hit (the socket has given up on its own). Anyone who wants to surface a
// user-visible toast independent of the current view can subscribe.
export const WEBSOCKET_GAVE_UP = "websocket-gave-up";

// A reconnect is considered "healthy" only if the socket stayed open at
// least this many ms. Shorter opens (server accept-then-close, LB draining,
// auth reject mid-handshake) don't reset the retry counter, so the 10-try
// ceiling still applies and we don't hammer the backend indefinitely.
const MIN_STABLE_OPEN_MS = 10000;

class WebSockets extends React.Component {
  constructor(props) {
    super(props);
    this.baseUrl = process.env.REACT_APP_SERVER_URL;
    this.endPoint = this.baseUrl.replace("http", "ws") + "/extension";
    this.connectTimerId = null;
    this.sock = null;
    this.connectionsCount = 0;
    this.maxConnectionsCount = 10;
    this.retrySubToken = null;
    // 🐛 2026-09-02 REVIEW-GAPS: track lifecycle so post-unmount async
    // callbacks and mid-reconnect handler races don't corrupt state.
    this._unmounted = false;
    // Guards against rapid Retry clicks or PubSub double-fire. A reconnect
    // is "in flight" from the moment we open a new socket until we've seen
    // either an onopen or the retry ceiling. While set, forceReconnect()
    // is a no-op.
    this._reconnectInFlight = false;
    // Timestamp of the most recent onopen. Used to gate the counter reset
    // on a minimum-stable-duration check (finding #2).
    this._lastOpenTs = 0;
  }

  // Detach the browser-owned event handlers BEFORE closing so the async
  // close event doesn't run our onclose logic — it would otherwise
  // schedule a spurious reconnect timer (post-unmount) or burn a retry
  // budget slot (mid-forceReconnect race).
  detachAndClose(sock) {
    if (!sock) return;
    try { sock.onopen = null; } catch (_e1) { /* best-effort */ }
    try { sock.onmessage = null; } catch (_e2) { /* best-effort */ }
    try { sock.onclose = null; } catch (_e3) { /* best-effort */ }
    try { sock.onerror = null; } catch (_e4) { /* best-effort */ }
    try {
      if (sock.readyState !== sock.CLOSED) sock.close();
    } catch (_e5) { /* best-effort */ }
  }

  // Safe status setter — never touches the parent after unmount.
  reportStatus(status) {
    if (this._unmounted) return;
    try { this.props.onWebSocketsChangeConnectedStatus(status); } catch (_e) { /* best-effort */ }
  }

  connect() {
    if (this._unmounted) return;
    // 🐛 2026-09-02 REVIEW-GAP #8: only skip when the socket is actively
    // CONNECTING or OPEN. A CLOSING socket is on its way out and won't
    // deliver more events — treat it like CLOSED and start a fresh
    // connection so the retry chain doesn't stall while a browser finishes
    // its half-close handshake.
    if (this.sock) {
      const rs = this.sock.readyState;
      if (rs === this.sock.CONNECTING || rs === this.sock.OPEN) {
        return;
      }
    }
    // 🐛 2026-09-02 REVIEW-GAP #1: set the in-flight flag AFTER the
    // constructor succeeds. If `new WebSocket()` throws (mixed-content
    // SecurityError on an https page pointing at ws://, malformed endpoint
    // after env change, browser socket quota, etc.) the flag would stay
    // stuck true forever, permanently disabling the Retry button.
    let sock;
    try {
      sock = new WebSocket(this.endPoint);
    } catch (ctorErr) {
      console.warn("[WebSockets] WebSocket constructor threw:", ctorErr);
      this._reconnectInFlight = false;
      this.reportStatus(false);
      return;
    }
    this._reconnectInFlight = true;
    this.sock = sock;

    sock.onopen = () => {
      if (this._unmounted || this.sock !== sock) {
        // Handlers should already be nulled by detachAndClose in these
        // paths — this is belt-and-suspenders for anything the browser
        // dispatched between the detach and close events.
        return;
      }
      this._reconnectInFlight = false;
      this._lastOpenTs = Date.now();
      // 🐛 2026-09-02 REVIEW-GAP #2: don't blindly reset the counter here.
      // A server that accept-then-immediately-closes would let us hammer it
      // forever. The onclose handler applies the MIN_STABLE_OPEN_MS check
      // and only resets after the socket has been stable long enough.
      this.reportStatus(true);
      window.clearTimeout(this.connectTimerId);
    };

    sock.onmessage = e => {
      if (this._unmounted || this.sock !== sock) return;
      // WebSocket messages indicate active usage — reset auto-logout timer
      AutoLogout.getInstance()?.recordApiActivity();

      const response = JSON.parse(e.data);
      if (response.command === "sendData") {
        PubSub.publish(eventNames.displayUpdateComponents);
        PubSub.publish(eventNames.componentDependencyDetaching);
        PubSub.publish("refreshCurrentComponent");
      }
    };

    sock.onclose = () => {
      // Ignore closes from an old socket we've already replaced or from
      // this component after unmount (finding #1, #3).
      if (this._unmounted || this.sock !== sock) return;

      // 🐛 2026-09-02 REVIEW-GAP #2: only reset the retry counter when the
      // just-closed socket was open for at least MIN_STABLE_OPEN_MS. Short
      // opens count toward the ceiling — this preserves the 10-try give-up.
      const wasOpen = this._lastOpenTs > 0;
      const openDuration = wasOpen ? Date.now() - this._lastOpenTs : 0;
      if (wasOpen && openDuration >= MIN_STABLE_OPEN_MS) {
        this.connectionsCount = 0;
      }
      this._lastOpenTs = 0;
      this._reconnectInFlight = false;
      // Clear any prior scheduled retry before overwriting the id
      // (finding #6 — orphaned setTimeout entries).
      window.clearTimeout(this.connectTimerId);
      this.connectTimerId = null;

      if (this.connectionsCount >= this.maxConnectionsCount) {
        // Give up automatic reconnect after 10 tries (~50s at 5s spacing) so
        // we don't spin forever against a dead backend. User can still
        // trigger a manual retry via WEBSOCKET_RETRY_EVENT.
        // 🐛 2026-09-02 REVIEW-GAP #4: publish a gave-up event so any view
        // (not just flow-node clicks in TheTree) can surface a toast telling
        // the user retries stopped. Before this, a user on a non-flow view
        // (project settings, groups, TCM) saw stale data indefinitely with
        // no indication that the connection was permanently dead.
        this.reportStatus(false);
        PubSub.publish(WEBSOCKET_GAVE_UP);
      } else {
        this.connectionsCount++;
        this.connectTimerId = window.setTimeout(() => this.connect(), 5000);
        this.reportStatus(false);
      }
    };
  }

  // Force a fresh reconnect: reset the retry counter and try again NOW,
  // regardless of whether we've already given up on the automatic loop.
  // Called from the Retry button on the "connection lost" toast.
  // Publishes WEBSOCKET_RETRY_ACK with the outcome so callers can surface
  // accurate feedback (see finding #2 in the review — the toast used to
  // silently no-op on rapid clicks).
  forceReconnect() {
    if (this._unmounted) {
      PubSub.publish(WEBSOCKET_RETRY_ACK, "failed");
      return;
    }
    if (this._reconnectInFlight) {
      // 🐛 2026-09-02 REVIEW-GAP #5: rapid Retry clicks / double-fired
      // PubSub events used to cascade-close each other's in-flight sockets
      // and race the fresh onclose against the fresh onopen. Skip if we're
      // already connecting.
      console.log("[WebSockets] 🔁 forceReconnect ignored — reconnect already in flight");
      PubSub.publish(WEBSOCKET_RETRY_ACK, "in-flight");
      return;
    }
    console.log("[WebSockets] 🔁 forceReconnect requested — resetting retry counter");
    window.clearTimeout(this.connectTimerId);
    this.connectTimerId = null;
    this.connectionsCount = 0;
    // Detach the old socket's handlers before close so its async onclose
    // doesn't leak into the new attempt's state (finding #3).
    this.detachAndClose(this.sock);
    this.sock = null;
    this.connect();
    // connect() may have failed synchronously (constructor throw) — check
    // the in-flight flag to know whether we actually started a reconnect.
    PubSub.publish(WEBSOCKET_RETRY_ACK, this._reconnectInFlight ? "accepted" : "failed");
  }

  componentDidMount() {
    this.connect();
    this.retrySubToken = PubSub.subscribe(WEBSOCKET_RETRY_EVENT, () => this.forceReconnect());
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this.retrySubToken) {
      PubSub.unsubscribe(this.retrySubToken);
      this.retrySubToken = null;
    }
    window.clearTimeout(this.connectTimerId);
    this.connectTimerId = null;
    // Detach handlers before close so the async close event can't run our
    // onclose and schedule a zombie reconnect on this unmounted component
    // (finding #1).
    this.detachAndClose(this.sock);
    this.sock = null;
  }

  render() {
    return false;
  }
}

export default WebSockets;
